import { useCallback, useEffect, useState } from "react";
import { Warehouse, CalendarDays, TrendingUp, RefreshCw } from "lucide-react";
import api, { fmtDate, fmtErr } from "../../lib/api";
import { toast } from "sonner";

const monthKey = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const monthLabel = (k) => new Date(`${k}-01T00:00:00`).toLocaleDateString("en-IN", { month: "short", year: "numeric" });

export default function AdminWarehouses() {
  const [warehouses, setWarehouses] = useState(null);
  const [syncing, setSyncing] = useState(null);

  const load = useCallback(() => api.get("/admin/warehouses").then(({ data }) => setWarehouses(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const resync = async (w) => {
    setSyncing(w.warehouse_id);
    try {
      const { data } = await api.post(`/admin/warehouses/${w.warehouse_id}/resync`);
      const failed = Object.entries(data.sync || {}).filter(([, s]) => s.status === "failed");
      if (failed.length) toast.error(`${w.name}: still failing — ${failed.map(([c, s]) => `${c}: ${(s.error || "").slice(0, 80)}`).join(" · ")}`, { duration: 10000 });
      else toast.success(`${w.name} synced to all providers`);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setSyncing(null); }
  };

  const now = new Date();
  const thisKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const byMonth = {};
  (warehouses || []).forEach((w) => {
    const k = monthKey(w.created_at);
    if (k) byMonth[k] = (byMonth[k] || 0) + 1;
  });
  const months = Object.keys(byMonth).sort().reverse();
  const maxMonth = Math.max(1, ...Object.values(byMonth));

  return (
    <div data-testid="admin-warehouses-page">
      <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Warehouses</h1>
      <p className="text-sm text-slate-500">All customer pickup locations — serials start at WH-1001.</p>

      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="wh-stat-total">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400"><Warehouse className="h-3.5 w-3.5 text-shipany-blue" /> Total Warehouses</div>
          <p className="mono mt-2 text-2xl font-bold text-shipany-navy">{warehouses ? warehouses.length : "—"}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="wh-stat-this-month">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400"><CalendarDays className="h-3.5 w-3.5 text-emerald-600" /> Created This Month</div>
          <p className="mono mt-2 text-2xl font-bold text-shipany-navy">{warehouses ? (byMonth[thisKey] || 0) : "—"}</p>
          <p className="mt-1 text-[11px] text-slate-400">{monthLabel(thisKey)}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="wh-stat-months">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-400"><TrendingUp className="h-3.5 w-3.5 text-shipany-cyan" /> Active Months</div>
          <p className="mono mt-2 text-2xl font-bold text-shipany-navy">{warehouses ? months.length : "—"}</p>
        </div>
      </div>

      {warehouses && months.length > 0 && (
        <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5" data-testid="wh-monthly-breakdown">
          <h2 className="font-display text-sm font-bold text-shipany-navy">Warehouses Created per Month</h2>
          <div className="mt-4 space-y-2.5">
            {months.slice(0, 12).map((k) => (
              <div key={k} className="flex items-center gap-3" data-testid={`wh-month-${k}`}>
                <span className="mono w-20 text-xs font-semibold text-slate-500">{monthLabel(k)}</span>
                <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-gradient-to-r from-shipany-blue to-shipany-cyan" style={{ width: `${(byMonth[k] / maxMonth) * 100}%` }} />
                </div>
                <span className="mono w-8 text-right text-xs font-bold text-shipany-navy">{byMonth[k]}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-5 overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="admin-warehouses-table">
        <table className="w-full min-w-[860px] text-sm">
          <thead>
            <tr className="bg-shipany-navy text-left text-xs uppercase tracking-wider text-white">
              <th className="px-5 py-3.5">Serial</th><th className="px-4 py-3.5">Warehouse</th><th className="px-4 py-3.5">Owner</th><th className="px-4 py-3.5">Address</th><th className="px-4 py-3.5">Pincode</th><th className="px-4 py-3.5">GST</th><th className="px-4 py-3.5">Created</th><th className="px-4 py-3.5">Provider Sync</th>
            </tr>
          </thead>
          <tbody>
            {(warehouses || []).map((w) => (
              <tr key={w.warehouse_id} className="border-t border-slate-100">
                <td className="px-5 py-3.5"><span className="mono rounded-lg bg-shipany-blue/10 px-2 py-1 text-xs font-bold text-shipany-blue" data-testid={`wh-serial-${w.warehouse_id}`}>{w.serial ? `WH-${w.serial}` : "—"}</span></td>
                <td className="px-4 py-3.5 font-semibold text-shipany-navy">{w.name} {w.is_default && <span className="ml-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-600">DEFAULT</span>}</td>
                <td className="px-4 py-3.5 text-xs text-slate-500">{w.owner_email}</td>
                <td className="px-4 py-3.5 text-xs text-slate-500">{w.address}, {w.city}</td>
                <td className="mono px-4 py-3.5">{w.pincode}</td>
                <td className="mono px-4 py-3.5 text-xs text-slate-500">{w.gst || "—"}</td>
                <td className="px-4 py-3.5 text-xs text-slate-500">{fmtDate(w.created_at)}</td>
                <td className="px-4 py-3.5">
                  <div className="flex flex-wrap items-center gap-1">
                    {Object.entries(w.provider_sync || {}).map(([code, s]) => (
                      <span key={code} title={s.error || s.ref || s.reason || ""} data-testid={`wh-sync-${w.warehouse_id}-${code}`}
                        className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase ${s.status === "synced" ? "bg-emerald-50 text-emerald-600" : s.status === "failed" ? "bg-red-50 text-red-500" : "bg-slate-100 text-slate-400"}`}>{code}</span>
                    ))}
                    <button onClick={() => resync(w)} disabled={syncing === w.warehouse_id} data-testid={`wh-resync-${w.warehouse_id}`}
                      className="ml-1 flex items-center gap-1 rounded-lg bg-shipany-blue/10 px-2 py-0.5 text-[10px] font-bold text-shipany-blue hover:bg-shipany-blue/20 disabled:opacity-50">
                      <RefreshCw className={`h-3 w-3 ${syncing === w.warehouse_id ? "animate-spin" : ""}`} /> Re-sync
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
